import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import SignIn from './components/Auth/SignIn';
import VerifyCode from './components/Auth/VerifyCode';
import AccountSetup from './components/Auth/AccountSetup';
import ShippingAddressConfirmation from './components/Auth/ShippingAddressConfirmation';
import ThankYouPreShipment from './components/Auth/ThankYouPreShipment';
import PreparingForShipping from './components/Auth/PreparingForShipping';
import IndividualDashboard from './components/IndividualDashboard/IndividualDashboard';
import HomeDashboard from './components/HomeDashboard/HomeDashboard';
import DeviceSetup from './components/DeviceSetup/DeviceSetup';
import DeviceSettings from './components/HomeDashboard/DeviceSettings';
import GuestDeviceSetup from './components/DeviceSetup/GuestDeviceSetup';
import ConfigureHiglos from './components/ConfigureHiglos/ConfigureHiglos';
import { onAuthStateChanged } from "firebase/auth";
import { auth, API_BASE_URL } from "./firebase/firebaseConfig";


const LoadingSpinner = () => (
  <div className="flex min-h-screen w-full flex-col items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

function App() {
  const [step, setStep] = useState('signin');
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState('');
  const [familyIDs, setFamilyIDs] = useState([]);
  const [familyName, setFamilyName] = useState('');
  const [numOfFamilies, setNumOfFamilies] = useState(0);
  const [selectedFamilyID, setSelectedFamilyID] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Shipping-related state
  const [shipped, setShipped] = useState(false);
  const [preparingForShipping, setPreparingForShipping] = useState(false);
  const [shippingAddressesConfirmed, setShippingAddressesConfirmed] = useState(false);
  const [nameFromServer, setNameFromServer] = useState(null);
  const [nameAtOrder, setNameAtOrder] = useState(null);
  const [shippingAddressAtOrder, setShippingAddressAtOrder] = useState(null);
  const [shippingCountryAtOrder, setShippingCountryAtOrder] = useState(null);
  const [justCompletedShipping, setJustCompletedShipping] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setIsAuthenticated(true);
        setEmail(currentUser.email);
        try {
          const idToken = await currentUser.getIdToken();
          const response = await fetch(`${API_BASE_URL}/user/userinfo?email=${encodeURIComponent(currentUser.email)}`, {
            headers: {
              'Authorization': `Bearer ${idToken}`
            }
          });
          const data = await response.json();

          if (data.needsSetup) {
            setUserType(data.userType);
            setFamilyIDs(data.familyIDs);
            setFamilyName(data.familyName);
            setNumOfFamilies(data.numOfFamilies);

            // Set shipping-related state
            setShipped(data.shipped || false);
            setPreparingForShipping(data.preparingForShipping || false);
            setShippingAddressesConfirmed(data.shippingAddressesConfirmed || false);
            setNameFromServer(data.name || null);
            setNameAtOrder(data.nameAtOrder || null);
            setShippingAddressAtOrder(data.shippingAddressAtOrder || null);
            setShippingCountryAtOrder(data.shippingCountryAtOrder || null);

            // Routing logic based on user type and shipped status
            if (data.userType === 'invitedUser') {
              // Invited users skip shipping entirely - go straight to account setup
              setStep('setup');
            } else if (data.shipped === true || data.shipped === 'true') {
              // Devices already shipped - go to name + family setup
              setStep('setup');
            } else if (data.preparingForShipping === true || data.preparingForShipping === 'true') {
              // Devices are being prepared for shipping - address can no longer be changed
              setStep('preparingForShipping');
            } else {
              // Devices not yet shipped - check if addresses confirmed
              if (!data.shippingAddressesConfirmed) {
                setStep('shippingSetup');
              } else {
                setStep('thankYou');
              }
            }
          } else {
            setStep('dashboard');
          }
        } catch (error) {
          console.error('Error fetching user info:', error);
        }
      } else {
        setIsAuthenticated(false);
        setStep('signin');
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleEmailSubmit = (email) => {
    setEmail(email);
    setStep('verify');
  };

  const handleVerificationSuccess = () => {
    setIsAuthenticated(true);
    setIsLoading(true);
    // Don't set step here - let onAuthStateChanged determine the correct page
  };

  const handleGoBackToSignIn = () => {
    setStep('signin');
  };

  const handleSetupComplete = () => {
    setStep('dashboard');
  };

  const handleShippingComplete = () => {
    setJustCompletedShipping(true);
    setStep('thankYou');
  };

  const handleManageFamily = (familyID) => {
    setSelectedFamilyID(familyID);
    setStep('home');
  };

  const handleSetupDevice = (familyID) => {
    setSelectedFamilyID(familyID);
    setStep('deviceSetup');
  };

  const handleConfigureHiglos = () => {
    setStep('configureHiglos');
  };

  const handleDeviceSettings = (familyID) => {
    setSelectedFamilyID(familyID);
    setStep('deviceSettings');
  };

  const handleReturn = () => {
    setStep('dashboard');
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <Routes>
        <Route path="/deviceSetupLocal" element={<GuestDeviceSetup />} />
        <Route path="/" element={
          step === 'signin' ? <SignIn onEmailSubmit={handleEmailSubmit} /> :
          step === 'verify' ? <VerifyCode email={email} onVerificationSuccess={handleVerificationSuccess} onGoBack={handleGoBackToSignIn} /> :
          step === 'shippingSetup' ? <ShippingAddressConfirmation email={email} numOfFamilies={numOfFamilies} shippingAddressAtOrder={shippingAddressAtOrder} shippingCountryAtOrder={shippingCountryAtOrder} onComplete={handleShippingComplete} /> :
          step === 'preparingForShipping' ? <PreparingForShipping email={email} /> :
          step === 'thankYou' ? <ThankYouPreShipment isReturningUser={!justCompletedShipping} /> :
          step === 'setup' ? <AccountSetup email={email} userType={userType} familyIDs={familyIDs} familyName={familyName} numOfFamilies={numOfFamilies} nameFromServer={nameFromServer} nameAtOrder={nameAtOrder} onSetupComplete={handleSetupComplete} /> :
          step === 'dashboard' ? <IndividualDashboard onManageFamily={handleManageFamily} onSetupDevice={handleSetupDevice} onDeviceSettings={handleDeviceSettings} onConfigureHiglos={handleConfigureHiglos} /> :
          step === 'configureHiglos' ? <ConfigureHiglos onReturn={handleReturn} /> :
          step === 'home' ? <HomeDashboard familyID={selectedFamilyID} onReturn={handleReturn} /> :
          step === 'deviceSettings' ? <DeviceSettings familyID={selectedFamilyID} onReturn={handleReturn} /> :
          step === 'deviceSetup' ? <DeviceSetup familyID={selectedFamilyID} onReturn={handleReturn} /> :
          <Navigate to="/" />
        } />
      </Routes>
    </Router>
  );
}

export default App;
